import React, { useState, useEffect } from 'react';
import { useLanguage } from '../App';
import { routineAPI } from '../lib/api';
import { runJob } from '../lib/jobs';
import { t } from '../lib/i18n';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { Calendar, Download, RefreshCw, Loader2, Sparkles } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

export default function RoutinePage() {
  const { language } = useLanguage();
  const [routine, setRoutine] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [downloading, setDownloading] = useState(false);

  useEffect(() => { loadRoutine(); }, []);

  const loadRoutine = async () => {
    try {
      const res = await routineAPI.get();
      setRoutine(res.data.routine || null);
    } catch (err) {
      console.error('Failed to load routine:', err);
      setRoutine(null);
    } finally {
      setLoading(false);
    }
  };

  const handleGenerate = async () => {
    setGenerating(true);
    const pending = toast.loading(t('routineInProgress', language));
    try {
      // background job + polling: immune to proxy timeouts on long generations
      const result = await runJob(() => routineAPI.generate(language));
      setRoutine({ content: result.routine });
      toast.dismiss(pending);
      toast.success(language === 'fr' ? 'Routine g\u00e9n\u00e9r\u00e9e!' : 'Routine generated!');
    } catch (err) {
      toast.dismiss(pending);
      toast.error(err.code === 'job_timeout' ? t('jobTimeout', language) : (language === 'fr' ? 'Erreur lors de la g\u00e9n\u00e9ration de la routine' : 'Error generating routine'));
    } finally {
      setGenerating(false);
    }
  };

  const handleDownloadPdf = async () => {
    setDownloading(true);
    try {
      const res = await routineAPI.downloadPdf(language);
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      
      // Use window.open instead of DOM manipulation to avoid React removeChild errors
      const newWindow = window.open(url, '_blank');
      if (!newWindow) {
        // Fallback: use a temporary hidden iframe approach
        const iframe = document.createElement('iframe');
        iframe.style.display = 'none';
        iframe.src = url;
        document.body.appendChild(iframe);
        setTimeout(() => {
          try { iframe.parentNode?.removeChild(iframe); } catch(e) { console.warn('Iframe cleanup:', e.message); }
          window.URL.revokeObjectURL(url);
        }, 5000);
      } else {
        setTimeout(() => window.URL.revokeObjectURL(url), 5000);
      }
      
      toast.success(language === 'fr' ? 'PDF téléchargé!' : 'PDF downloaded!');
    } catch (err) {
      toast.error(language === 'fr' ? 'Erreur de téléchargement' : 'Error downloading PDF');
    } finally {
      setDownloading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="w-8 h-8 border-2 border-[var(--lux-gold)] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="px-4 sm:px-6 lg:px-8 py-6 space-y-6">
      <div className="fade-in">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="font-display text-3xl text-white">{t('routine', language)}</h1>
            <p className="text-white/50 text-sm mt-1">
              {language === 'fr' ? 'Votre routine hebdomadaire personnalis\u00e9e' : 'Your personalized weekly routine'}
            </p>
          </div>
          <div className="flex gap-2">
            {routine?.content && (
              <Button
                data-testid="weekly-routine-download-pdf-button"
                onClick={handleDownloadPdf}
                disabled={downloading}
                variant="outline"
                className="bg-[var(--lux-panel)] border-white/10 text-white hover:bg-[var(--lux-panel-2)]"
              >
                {downloading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}
                {t('downloadPdf', language)}
              </Button>
            )}
            <Button
              data-testid="generate-routine-button"
              onClick={handleGenerate}
              disabled={generating}
              className="bg-[var(--lux-gold)] text-[var(--lux-ink)] hover:bg-[var(--lux-gold-2)] active:scale-[0.98] transition-[background-color,transform] duration-200"
            >
              {generating ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-2" />}
              {generating ? t('generating', language) : t('generateNew', language)}
            </Button>
          </div>
        </div>
      </div>

      {/* Routine Content */}
      {routine?.content ? (
        <Card className="bg-lux-panel/90 border-white/[0.08]">
          <CardContent className="pt-6">
            <div className="ai-response prose prose-invert max-w-none">
              <ReactMarkdown remarkPlugins={[remarkGfm]}>{routine.content}</ReactMarkdown>
            </div>
          </CardContent>
        </Card>
      ) : (
        <Card className="bg-lux-panel/90 border-white/[0.08]">
          <CardContent className="py-16">
            <div className="flex flex-col items-center text-center">
              <div className="p-4 rounded-2xl bg-lux-gold/10 mb-4">
                <Calendar className="w-8 h-8 text-[var(--lux-gold)]" />
              </div>
              <h3 className="font-display text-xl text-white mb-2">{t('noRoutine', language)}</h3>
              <p className="text-white/40 text-sm max-w-md mb-6">
                {language === 'fr'
                  ? 'G\u00e9n\u00e9rez votre premi\u00e8re routine hebdomadaire personnalis\u00e9e bas\u00e9e sur votre profil.'
                  : 'Generate your first personalized weekly routine based on your profile.'}
              </p>
              <Button
                onClick={handleGenerate}
                disabled={generating}
                className="bg-[var(--lux-gold)] text-[var(--lux-ink)] hover:bg-[var(--lux-gold-2)]"
              >
                {generating ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Sparkles className="w-4 h-4 mr-2" />}
                {generating ? t('generating', language) : t('generateNew', language)}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
